import { useEffect, useRef, useState } from 'react'
import type { LocationPoint } from '../types/index'

export function useGeolocation(highAccuracy: boolean = true) {
  const [location, setLocation] = useState<LocationPoint | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [watching, setWatching] = useState(false)
  const watchIdRef = useRef<number | null>(null)
  const watchingRef = useRef(false)
  const highAccuracyRef = useRef(highAccuracy)

  // Keep ref in sync with prop
  useEffect(() => { highAccuracyRef.current = highAccuracy }, [highAccuracy])

  const clearWatch = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current)
      watchIdRef.current = null
    }
  }

  const startTracking = () => {
    if (!('geolocation' in navigator)) {
      setError('Geolocation not supported')
      return
    }

    clearWatch()
    setWatching(true)
    watchingRef.current = true
    setError(null)

    watchIdRef.current = navigator.geolocation.watchPosition(
      position => {
        const { latitude, longitude, accuracy } = position.coords
        setLocation({
          latitude,
          longitude,
          accuracy,
          timestamp: new Date().toISOString(),
        })
        setError(null)
      },
      err => {
        setError(err.message)
        setWatching(false)
        watchingRef.current = false
      },
      {
        enableHighAccuracy: highAccuracyRef.current,
        timeout: 10000,
        maximumAge: highAccuracyRef.current ? 0 : 30000,
      }
    )
  }

  const stopTracking = () => {
    clearWatch()
    setWatching(false)
    watchingRef.current = false
  }

  // Restart watch when accuracy mode changes (only if already watching)
  useEffect(() => {
    if (watchingRef.current) {
      startTracking()
    }
  }, [highAccuracy]) // eslint-disable-line react-hooks/exhaustive-deps

  // Permission check on mount
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.permissions?.query({ name: 'geolocation' }).then(permission => {
        if (permission.state === 'denied') {
          setError('Geolocation permission denied')
        }
      })
    }

    return () => clearWatch()
  }, [])

  return {
    location,
    error,
    watching,
    startTracking,
    stopTracking,
  }
}
